import React, { type ReactElement, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router'
import { Typography, Divider, Skeleton } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import { object, string, type InferType } from 'yup'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import backgroundGraphics from 'assets/backgroundGraphics.svg'
import { StoreStatus } from 'common/utils/types'
import Message from 'common/authentication/Message'
import LabeledTextfield from 'common/ui/labeled-textfield/LabeledTextfield'

import { register, signIn } from '../auth.service'

import styles from './Register.module.scss'

const registrationSchema = object({
  email: string().email('Neispravan email').required('Obavezno polje'),
  tin: string()
    .trim()
    .required('Obavezno polje')
    .matches(/^\d{9}$/, { excludeEmptyString: true, message: 'PIB mora da sadrži 9 brojeva' }),
  website: string().trim().url('Polje mora biti validan URL'),
})

export type RegisterForm = InferType<typeof registrationSchema>

const Register: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // don't render content until we know we want to display the registration screen
  const [loading, setLoading] = useState(true)
  const { createAccountStatus, status, isAuthenticated, user } = useAppSelector((state) => state.auth)

  const loginData = {
    email: searchParams.get('email') ?? '',
    password: searchParams.get('token') ?? '',
  }

  const formik = useFormik({
    initialValues: {
      email: loginData.email ?? '',
      tin: '',
    },
    validationSchema: registrationSchema,
    onSubmit: async (values: RegisterForm) => await dispatch(register(values)),
  })

  useEffect(() => {
    if (loginData.email && loginData.password && !isAuthenticated) {
      void dispatch(signIn(loginData)).finally(() => {
        setLoading(false)
      })
    } else {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user && user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      navigate('/auth/set-password', { state: { email: loginData.email } })
      return
    }
    if (isAuthenticated) {
      navigate('/')
    }
  }, [loginData.email, status, isAuthenticated])

  return loading ? (
    <div className={styles.container}>
      <div>
        <Skeleton variant="text" sx={{ fontSize: '56px' }} width={550} />
        <Skeleton variant="text" sx={{ fontSize: '56px' }} width={260} />
      </div>
      <div className={styles.formContainer}>
        <Skeleton
          variant="rectangular"
          width={646}
          height={searchParams.get('email') !== null ? 438 : 495}
          sx={{ borderRadius: '10px' }}
        />
      </div>
    </div>
  ) : (
    <div className={styles.container} style={{ backgroundImage: `url(${backgroundGraphics})` }}>
      <Typography variant="title11">
        Dobrodošli na <span style={{ fontWeight: 700 }}>CDT Vendors</span> platformu
      </Typography>

      {createAccountStatus !== StoreStatus.Succeeded ? (
        <div className={styles.formContainer}>
          <Message />
          <form onSubmit={formik.handleSubmit} className={styles.form}>
            <Typography variant="title1" className={styles.title}>
              {' '}
              <img src={process.env.PUBLIC_URL + '/logo192.png'} alt="logo" width={20} /> Popunite neophodne
              podatke za registraciju
            </Typography>

            <Divider className={styles.divider} flexItem />

            <LabeledTextfield
              id="email"
              name="email"
              label="*Email"
              placeholder="email"
              type="email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              error={formik.touched.email && !!formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
              style={{ width: '100%' }}
            />
            <div className={styles.formSubContainer}>
              <LabeledTextfield
                id="tin"
                name="tin"
                label="*PIB"
                placeholder="broj"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.tin}
                error={formik.touched.tin && !!formik.errors.tin}
                helperText={formik.touched.tin && formik.errors.tin}
                style={{ width: '40%' }}
              />
              <LabeledTextfield
                id="website"
                name="website"
                label="Website link"
                placeholder="https://www.websitelink.com"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.website ?? ''}
                error={formik.touched.website && !!formik.errors.website}
                helperText={formik.touched.website && formik.errors.website}
                style={{ flexGrow: 1 }}
              />
            </div>

            <Divider className={styles.divider} flexItem />
            <div className={styles.footer}>
              <LoadingButton
                type="submit"
                loading={status === StoreStatus.Loading}
                disabled={!formik.isValid || !formik.dirty}
                variant="primary"
                sx={{
                  width: 248,
                }}
              >
                Registrujte se
              </LoadingButton>
              <Link to="/auth/login">
                <Typography variant="title3">Imate nalog? Ulogujte se ovde</Typography>
              </Link>
            </div>
          </form>
        </div>
      ) : (
        <Typography variant="description4" className={styles.successContainer}>
          Uspešno ste se registrovali.
          <br />
          Ukoliko niste do sad imali profil na našoj platformi dobićete putem mejla link pomoću kojeg možete
          kreirati svoju lozinku.
        </Typography>
      )}
    </div>
  )
}

export default Register
